
import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'

/**
 * AuctionCountdownTimer - Live countdown timer for auction end time.
 * 
 * BRD: "Indicate how much time remaining in current iteration (countdown clock) - 
 * this will be important when there is time added at the end of an auction if there 
 * are bids at 2:59PM. If there is time added notification/flag that extended time 
 * had ended so we know when we can sell/counter if required"
 */
const AuctionCountdownTimer = ({ 
	endTime,           // ISO date string or Date object
	secondsRemaining,  // Optional: Pre-calculated seconds from server
	isExtended,        // Whether auction time was extended due to late bids
	onExpired,         // Callback when timer expires
	showDate = false   // Show date portion for end times > 24 hours
}) =>
{
	const [timeRemaining, setTimeRemaining] = useState(null)
	
	useEffect(() => {
		// Calculate initial time remaining
		const calculateRemaining = () => {
			if (secondsRemaining !== undefined && secondsRemaining !== null) {
				return secondsRemaining
			}
			if (!endTime) return null
			
			const end = new Date(endTime)
			const now = new Date()
			const diffMs = end.getTime() - now.getTime()
			return Math.max(0, Math.floor(diffMs / 1000))
		}
		
		setTimeRemaining(calculateRemaining())
		
		// Update every second
		const interval = setInterval(() => {
			setTimeRemaining(prev => {
				if (prev === null) return calculateRemaining()
				if (prev <= 0) {
					if (onExpired) onExpired()
					return 0
				}
				return prev - 1
			})
		}, 1000)
		
		return () => clearInterval(interval)
	}, [endTime, secondsRemaining, onExpired])
	
	// Format time remaining
	const formatTime = (seconds) => {
		if (seconds === null || seconds === undefined) return '—'
		if (seconds <= 0) return 'Ended'
		
		const days = Math.floor(seconds / 86400)
		const hours = Math.floor((seconds % 86400) / 3600)
		const minutes = Math.floor((seconds % 3600) / 60)
		const secs = seconds % 60
		
		if (days > 0) {
			return `${days}d ${hours}h ${minutes}m`
		}
		if (hours > 0) {
			return `${hours}h ${minutes}m ${secs}s`
		}
		if (minutes > 0) {
			return `${minutes}m ${secs}s`
		}
		return `${secs}s`
	}
	
	// Determine urgency styling
	const getUrgencyStyle = () => {
		if (timeRemaining === null) return {}
		if (timeRemaining <= 0) return { color: '#6c757d' }  // Gray for ended
		if (timeRemaining <= 300) return { color: '#dc3545', fontWeight: 'bold' }  // Red for < 5 min
		if (timeRemaining <= 900) return { color: '#fd7e14', fontWeight: '600' }   // Orange for < 15 min
		if (timeRemaining <= 3600) return { color: '#ffc107' }  // Yellow for < 1 hour
		return {}
	}
	
	// Format end date/time for display
	const formatEndDateTime = () => {
		if (!endTime) return ''
		const end = new Date(endTime)
		if (isNaN(end.getTime())) return ''
		
		const timeStr = end.toLocaleTimeString('en-US', { 
			hour: 'numeric', 
			minute: '2-digit',
			hour12: true 
		})
		
		if (showDate || timeRemaining > 86400) {
			const dateStr = end.toLocaleDateString('en-US', { 
				month: 'short', 
				day: 'numeric' 
			})
			return `${dateStr} ${timeStr}`
		}
		return timeStr
	}
	
	return (
		<div className="auction-countdown-timer">
			{/* Countdown display */}
			<div style={{ ...getUrgencyStyle(), fontFamily: 'monospace' }}>
				{formatTime(timeRemaining)}
			</div>
			
			{/* End time display */}
			{endTime && (
				<div style={{ fontSize: '0.75em', color: '#6c757d' }}>
					Ends: {formatEndDateTime()}
				</div>
			)}
			
			{/* Extended time indicator per BRD */}
			{isExtended && timeRemaining > 0 && (
				<div 
					style={{ 
						fontSize: '0.7em', 
						color: '#dc3545', 
						fontWeight: 'bold',
						marginTop: '2px'
					}}
					title="Auction time was extended due to late bidding"
				>
					⏱ EXTENDED
				</div>
			)}
		</div>
	)
}

AuctionCountdownTimer.propTypes = {
	/** End time as ISO string or Date object */
	endTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
	/** Pre-calculated seconds remaining from server */
	secondsRemaining: PropTypes.number,
	/** Whether auction time was extended due to late bids */
	isExtended: PropTypes.bool,
	/** Callback when timer reaches zero */
	onExpired: PropTypes.func,
	/** Show date portion for end times > 24 hours */
	showDate: PropTypes.bool
}

export default AuctionCountdownTimer

